import React, { useEffect, useMemo, useState } from "react";
import MessengerLayout        from "../../layout/MessengerLayout";
import PayslipCard            from "../../components/payroll/PayslipCard";
import ActionPanel            from "../../components/payroll/ActionPanel";
import SalaryBreakdownModal   from "../../components/payroll/SalaryBreakdownModal";
import CTCPopup               from "../../components/payroll/CTCPopup";
import CorrectionModal        from "../../components/payroll/CorrectionModal";
import IncomeRequestModal     from "../../components/payroll/IncomeRequestModal";
import TaxDeclaration         from "../../components/payroll/TaxDeclaration";
import Form16Section          from "../../components/payroll/Form16Section";
import UkPayrollOverview      from "../../components/payroll/UkPayrollOverview";
import SalaryChangeLog        from "../../components/payroll/SalaryChangeLog";
import ExpensesPage           from "../expenses/ExpensesPage";
import BenefitsPage           from "../benefits/BenefitsPage";
import PayslipViewer          from "../../components/payroll/PayslipViewer";
import { ErrorState }         from "../../components/ui/LoadingStates";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

// Phase 5 — Payroll page acts as the container for Expenses + Benefits
// via tabs. The standalone /messenger/expenses and /messenger/benefits
// routes redirect here with ?tab= set.
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "payslips", label: "Payslips" },
  { key: "expenses", label: "Expenses" },
  { key: "benefits", label: "Benefits" },
];

export default function PayrollMessengerPage() {
  const _initialTab = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const t  = sp.get("tab");
      if (t && TABS.some(x => x.key === t)) return t;
    } catch {}
    return "overview";
  }, []);
  const [tab, setTab] = useState(_initialTab);

  const [payslips,     setPayslips]     = useState([]);
  const [slipsLoading, setSlipsLoading] = useState(true);
  const [error,        setError]        = useState("");
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [selectedYear,    setSelectedYear]    = useState("");

  const [showBreakdown,  setShowBreakdown]  = useState(false);
  const [showCTC,        setShowCTC]        = useState(false);
  const [showCorrection, setShowCorrection] = useState(false);
  const [showIncome,     setShowIncome]     = useState(false);

  const [taxRegime,             setTaxRegime]             = useState("Old");
  const [investmentDeclaration, setInvestmentDeclaration] = useState("");
  const [investmentProofFile,   setInvestmentProofFile]   = useState(null);

  // Phase 4a — payroll region gate. null until loaded; then
  // "UK" | "India" | "unknown". Decides WHICH region's fields show.
  const [region,       setRegion]       = useState(null);
  const [regionData,   setRegionData]   = useState(null);
  const [regionLoaded, setRegionLoaded] = useState(false);

  const token = localStorage.getItem("token") || "";

  const loadPayslips = () => {
    setSlipsLoading(true);
    setError("");
    fetch(`${API_BASE}/api/payroll/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setPayslips(list);
        if (list.length) setSelectedYear(list[0].financialYear);
      })
      .catch(() => setError("Unable to load payroll data. Please try again."))
      .finally(() => setSlipsLoading(false));
  };

  const loadRegion = () => {
    fetch(`${API_BASE}/api/payroll/my-region`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setRegion(d.payroll_region || "unknown"); setRegionData(d); })
      .catch(() => { setRegion("unknown"); setRegionData(null); })
      .finally(() => setRegionLoaded(true));
  };

  useEffect(() => {
    if (!token) { setSlipsLoading(false); setRegionLoaded(true); return; }
    loadPayslips();
    loadRegion();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["payslip_correction", "income_certificate", "bank_update"].includes(t)) {
        loadPayslips();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => window.removeEventListener("kira_action_complete", handleKiraAction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectPayslip = (id, yrOverride = null) => {
    if (yrOverride) { setSelectedYear(yrOverride); return; }
    const slip = payslips.find(p => p.id === id);
    setSelectedPayslip(slip || null);
    setShowBreakdown(true);
  };

  const handleTaxSubmit = async () => {
    if (!investmentProofFile) throw new Error("Please upload proof before submitting.");
    // Backend signature: POST /api/payroll/tax-declaration with
    // taxRegime + investmentDeclaration as query params and the proof
    // file in the multipart body under the field name `file`.
    const body = new FormData();
    body.append("file", investmentProofFile);
    const qs = new URLSearchParams({
      taxRegime,
      ...(investmentDeclaration ? { investmentDeclaration } : {}),
    }).toString();
    const res = await fetch(`${API_BASE}/api/payroll/tax-declaration?${qs}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  };

  return (
    <MessengerLayout>
      <div className="p-6 space-y-5 text-white">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Payroll</h1>
          <p className="text-white/40 text-sm mt-0.5">Payslips, tax, expenses and benefits</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/10">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                "px-4 py-2 text-sm font-medium transition border-b-2 -mb-px " +
                (tab === t.key
                  ? "text-[#00ff90] border-[#00ff90]"
                  : "text-white/60 border-transparent hover:text-white/90")
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && (
          <ErrorState
            message="Couldn't load payroll data"
            detail={error}
            onRetry={loadPayslips}
          />
        )}

        {tab === "overview" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <PayslipCard
                payslips={payslips}
                selectedYear={selectedYear}
                onSelect={handleSelectPayslip}
                loading={slipsLoading}
              />
              <ActionPanel
                onCorrection={() => setShowCorrection(true)}
                onIncome={() => setShowIncome(true)}
                onCTC={() => setShowCTC(true)}
                region={region || "unknown"}
              />
            </div>
            <SalaryChangeLog />

            {/* Phase 4a — region-gated statutory section. Previously the
                India-specific tax-regime + Form 16 blocks rendered for
                everyone (so UK employees like Aisha wrongly saw Indian
                fields). Now gated by the employee's payroll region. */}
            {!regionLoaded ? (
              <div className="h-24 rounded-2xl bg-white/5 animate-pulse" />
            ) : region === "India" ? (
              <>
                <TaxDeclaration
                  taxRegime={taxRegime}
                  setTaxRegime={setTaxRegime}
                  investmentDeclaration={investmentDeclaration}
                  setInvestmentDeclaration={setInvestmentDeclaration}
                  investmentProofFile={investmentProofFile}
                  setInvestmentProofFile={setInvestmentProofFile}
                  onSubmit={handleTaxSubmit}
                />
                <Form16Section />
              </>
            ) : region === "UK" ? (
              <UkPayrollOverview
                uk={regionData?.uk_payroll}
                regionSource={regionData?.region_source}
              />
            ) : (
              <section className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-5">
                <h2 className="text-amber-400 font-semibold">Payroll region not set</h2>
                <p className="text-white/50 text-sm mt-1">
                  We couldn’t determine whether your payroll is UK or India, so we’re not
                  showing region-specific fields. An admin can set your region in Payroll
                  Details.
                </p>
              </section>
            )}
          </>
        )}

        {tab === "payslips" && (
          <PayslipViewer />
        )}

        {tab === "expenses" && (
          <ExpensesPage embedded={true} />
        )}

        {tab === "benefits" && (
          <BenefitsPage embedded={true} />
        )}

      </div>

      {/* Modals */}
      <SalaryBreakdownModal payslip={showBreakdown ? selectedPayslip : null} onClose={() => setShowBreakdown(false)} region={region || "unknown"} />
      <CTCPopup visible={showCTC} payslips={payslips} selectedYear={selectedYear} onClose={() => setShowCTC(false)} region={region || "unknown"} />
      <CorrectionModal visible={showCorrection} onClose={() => setShowCorrection(false)} />
      <IncomeRequestModal visible={showIncome} onClose={() => setShowIncome(false)} />
    </MessengerLayout>
  );
}